import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchReceipt, type ReceiptResponse } from '../api'
import {
  Badge,
  Card,
  ErrorPanel,
  Footer,
  FullPageLoader,
  Header,
  Page,
} from '../_shared/ui'
import { formatDateTime, formatMoney } from '../_shared/format'

function statusBadge(status: ReceiptResponse['status']) {
  switch (status) {
    case 'settled': return <Badge variant="success">Settled</Badge>
    case 'pending': return <Badge variant="info">Pending</Badge>
    case 'processing': return <Badge variant="info">Processing</Badge>
    case 'failed': return <Badge variant="danger">Failed</Badge>
    case 'refunded': return <Badge variant="warning">Refunded</Badge>
    case 'test': return <Badge variant="warning">Test mode</Badge>
  }
}

function Row({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0' }}>
      <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>{label}</span>
      <span
        className={mono ? 'cp-mono cp-tabular' : 'cp-tabular'}
        style={{
          fontSize: 'var(--cp-text-sm)',
          color: 'var(--cp-fg-primary)',
          textAlign: 'right',
          wordBreak: 'break-all',
          maxWidth: '60%',
        }}
      >
        {children}
      </span>
    </div>
  )
}

export default function Receipt() {
  const { receiptId = '' } = useParams()
  const [receipt, setReceipt] = useState<ReceiptResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!receiptId) {
      setError('Receipt id missing from URL.')
      setLoading(false)
      return
    }
    let cancelled = false
    fetchReceipt(receiptId).then((res) => {
      if (cancelled) return
      if (!res.ok) {
        setError(
          res.error === 'receipt_not_found'
            ? 'We could not find this receipt. The link may be incorrect.'
            : `Unable to load receipt (${res.error}).`,
        )
      } else {
        setReceipt(res.data)
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [receiptId])

  if (loading) return <FullPageLoader />
  if (error) {
    return (
      <Page>
        <Header />
        <ErrorPanel title="Cannot load receipt" body={error} />
        <Footer />
      </Page>
    )
  }
  if (!receipt) return null

  const methodLabel: Record<ReceiptResponse['method'], string> = {
    sepa: 'SEPA Instant (EUR)',
    usdc: 'USDC on Polygon',
    gbp_fps: 'UK Faster Payments (GBP)',
    usd_ach: 'US ACH (USD)',
    lightning: 'Bitcoin Lightning',
  }

  return (
    <Page width={520}>
      <Header surveyName={receipt.surveyName} sponsorName={`Tier · ${receipt.tier}`} badge={statusBadge(receipt.status)} />

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>Amount</div>
          <div className="cp-tabular" style={{ fontSize: 'var(--cp-text-3xl)', fontWeight: 600 }}>
            {formatMoney(receipt.amount, receipt.currency)}
          </div>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Row label="Receipt id" mono>
            {receipt.receiptId}
          </Row>
          <Row label="Method">{methodLabel[receipt.method]}</Row>
          <Row label="Submitted">{formatDateTime(receipt.submittedAt)}</Row>
          <Row label="Settled">{formatDateTime(receipt.settledAt)}</Row>
        </div>
      </Card>

      {receipt.method === 'lightning' && receipt.lightning && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>Lightning settlement</span>
              {receipt.lightning.preimage ? (
                <Badge variant="success">Proof of payment</Badge>
              ) : (
                <Badge variant="info">Settling</Badge>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {receipt.lightning.sats != null && (
                <Row label="Sats paid" mono>
                  {receipt.lightning.sats.toLocaleString('en-GB')} sats
                </Row>
              )}
              {receipt.lightning.fxRate != null && (
                <Row label="FX rate">
                  1 BTC = €{receipt.lightning.fxRate.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </Row>
              )}
              {receipt.lightning.fxSource && (
                <Row label="FX source">{receipt.lightning.fxSource}</Row>
              )}
              {receipt.lightning.fxTimestamp && (
                <Row label="Rate quoted">{formatDateTime(receipt.lightning.fxTimestamp)}</Row>
              )}
              {receipt.lightning.paymentHash && (
                <Row label="Payment hash" mono>{receipt.lightning.paymentHash}</Row>
              )}
              {receipt.lightning.preimage && (
                <Row label="Preimage" mono>{receipt.lightning.preimage}</Row>
              )}
            </div>
            <div style={{ fontSize: 'var(--cp-text-xs)', color: 'var(--cp-fg-muted)' }}>
              On Lightning, the preimage is cryptographic proof that the sats were delivered to
              your wallet — equivalent to a signed receipt from the network itself.
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>Verifiable</span>
            {receipt.verifiable ? (
              <Badge variant="success">Anchored on-chain</Badge>
            ) : (
              <Badge variant="info">Anchor pending</Badge>
            )}
          </div>
          {receipt.verifiable && receipt.hash ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Row label="Mandate hash" mono>{receipt.hash.mandate}</Row>
              <Row label="Anchor hash" mono>{receipt.hash.anchor}</Row>
              <Row label="Anchor tx" mono>{receipt.hash.anchorTx}</Row>
              <Row label="Anchor chain">{receipt.hash.anchorChain}</Row>
            </div>
          ) : (
            <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
              The on-chain anchor is added once the payout settles. Return to this page after a
              few minutes to see the mandate hash and the on-chain transaction.
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontWeight: 600 }}>VAT / Tax statute</div>
          <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-secondary)' }}>
            {receipt.statutePlain}
          </div>
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
              Show legal reference
            </summary>
            <div
              style={{
                marginTop: 8,
                padding: '8px 10px',
                background: 'var(--cp-bg-code)',
                borderRadius: 'var(--cp-radius-sm)',
                fontFamily: 'var(--cp-font-mono)',
                fontSize: 'var(--cp-text-xs)',
              }}
            >
              {receipt.statute}
            </div>
          </details>
        </div>
      </Card>

      <Footer />
    </Page>
  )
}
